import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Lock, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { clientAuthService, ResetPasswordDto } from '@/services/client-auth.service';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, useToast } from '@caruso/ui';

export function ClientResetPassword() {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { toast } = useToast();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  const resetPasswordMutation = useMutation({
    mutationFn: (data: ResetPasswordDto) => clientAuthService.resetPassword(data),
    onSuccess: (data) => {
      setResetSuccess(true);
      toast({
        title: 'Senha redefinida!',
        description: data.message,
        variant: 'success',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao redefinir senha',
        description: error.response?.data?.message || 'Token inválido ou expirado',
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Por favor, preencha todos os campos',
        variant: 'destructive',
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: 'Senha muito curta',
        description: 'A senha deve ter no mínimo 6 caracteres',
        variant: 'destructive',
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: 'Senhas não conferem',
        description: 'As senhas digitadas não são iguais',
        variant: 'destructive',
      });
      return;
    }

    if (!token) {
      toast({
        title: 'Token inválido',
        description: 'Link de recuperação inválido',
        variant: 'destructive',
      });
      return;
    }

    resetPasswordMutation.mutate({ token, password });
  };

  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Card className="bg-neutral-900/50 backdrop-blur border-neutral-800">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-brand-gold/10 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-brand-gold" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Senha Redefinida!</h2>
                <p className="text-neutral-400 mb-6">
                  Sua senha foi alterada com sucesso. Você já pode fazer login com sua nova senha.
                </p>
                <Button
                  onClick={() => navigate('/client/login')}
                  className="w-full bg-brand-gold hover:bg-brand-gold/90 text-black font-semibold"
                >
                  Ir para o Login
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Card className="bg-neutral-900/50 backdrop-blur border-neutral-800">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Link Inválido</h2>
                <p className="text-neutral-400 mb-6">
                  O link de recuperação de senha é inválido ou está incompleto.
                </p>
                <Button
                  onClick={() => navigate('/client/forgot-password')}
                  className="w-full bg-brand-gold hover:bg-brand-gold/90 text-black font-semibold"
                >
                  Solicitar Novo Link
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="bg-neutral-900/50 backdrop-blur border-neutral-800">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-brand-gold/10 rounded-full flex items-center justify-center">
                <Lock className="w-8 h-8 text-brand-gold" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-white">Nova Senha</CardTitle>
            <CardDescription className="text-neutral-400">
              Digite sua nova senha
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-300">
                  Nova Senha
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <Input
                    type="password"
                    placeholder="Mínimo 6 caracteres"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-neutral-800/50 border-neutral-700 text-white placeholder:text-neutral-500"
                    disabled={resetPasswordMutation.isPending}
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-300">
                  Confirmar Senha
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <Input
                    type="password"
                    placeholder="Digite a senha novamente"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 bg-neutral-800/50 border-neutral-700 text-white placeholder:text-neutral-500"
                    disabled={resetPasswordMutation.isPending}
                    required
                  />
                </div>
              </div>

              <div className="bg-neutral-800/50 border border-brand-gold/20 rounded-lg p-3">
                <p className="text-xs text-neutral-400">
                  <strong className="text-brand-gold">Importante:</strong> Sua senha deve ter no mínimo 6 caracteres.
                  Escolha uma senha forte e que você não use em outros sites.
                </p>
              </div>

              <Button
                type="submit"
                className="w-full bg-brand-gold hover:bg-brand-gold/90 text-black font-semibold"
                disabled={resetPasswordMutation.isPending}
              >
                {resetPasswordMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Redefinindo...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Redefinir Senha
                  </>
                )}
              </Button>

              <div className="text-center text-sm text-neutral-400 mt-4">
                Lembrou sua senha?{' '}
                <Link
                  to="/client/login"
                  className="text-brand-gold hover:text-brand-gold/80 font-medium"
                >
                  Fazer login
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Link
            to="/"
            className="text-sm text-neutral-500 hover:text-neutral-400"
          >
            Voltar para o site
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
